import { parseString, parseRecord, notEmpty, handleRejection } from "@de-xima/xima-common-js-lang";
import $ from "jquery";

import { FrameBusChildHandler, getInstance as getFramebusInstance } from "./FramebusHandler.ts";
import { showNotification } from "./Notification.ts";
import { getWidgetByVar, runRemoteCommand } from "./Util.ts";

interface IHelpWidgetConfig {
  i18n: {
    downvoteDetail: string;
    downvoteSummary: string;
    upvoteDetail: string;
    upvoteSummary: string;
  };
  permissions: {
    canAskSupport: boolean;
  };
  widgets: {
    helpConsent: string;
    helpDialog: string;
    helpImage: string;
    supportDialog: string;
  };
}

let HelpConfig: IHelpWidgetConfig = {
  i18n: {
    downvoteDetail: "",
    downvoteSummary: "",
    upvoteDetail: "",
    upvoteSummary: "",
  },
  permissions: {
    canAskSupport: false,
  },
  widgets: {
    helpConsent: "",
    helpDialog: "",
    helpImage: "",
    supportDialog: "",
  },
};

function handleArticleDownvote(): void {
  if (HelpConfig.permissions.canAskSupport) {
    handleRejection(runRemoteCommand(BackendCommand?.openSupportDialog));
  } else {
    getWidgetByVar(HelpConfig.widgets.helpDialog, PrimeFaces.widget.Dialog)?.hide();
    showNotification(
      {
        detail: HelpConfig.i18n.downvoteDetail,
        severity: "info",
        summary: HelpConfig.i18n.downvoteSummary,
      },
      undefined,
      false,
    );
  }
}

function handleArticleUpvote(): void {
  getWidgetByVar(HelpConfig.widgets.helpDialog, PrimeFaces.widget.Dialog)?.hide();
  showNotification(
    {
      detail: HelpConfig.i18n.upvoteDetail,
      severity: "info",
      summary: HelpConfig.i18n.upvoteSummary,
    },
    undefined,
    false,
  );
}

function onHelpImageCloseClicked(event: JQuery.TriggeredEvent): void {
  // Prevent endless loop / stack overflow when we fire another click on one of our children
  $(event.currentTarget).off("click", onHelpImageCloseClicked);

  // Close the galleria lightbox
  $(event.currentTarget).find("button.ui-galleria-close").trigger("click");
}

function handleOpenLightboxImage(data: Record<string, unknown>): void {
  const imageUrl = parseString(data.imageUrl);
  const widget = getWidgetByVar(HelpConfig.widgets.helpImage, PrimeFaces.widget.Galleria);
  if (widget !== undefined) {
    widget.getJQ().find(".ui-galleria-item img").attr("src", imageUrl);
    widget.show();
    widget.jq.closest(".ui-galleria-mask").off("click", onHelpImageCloseClicked).on("click", onHelpImageCloseClicked);
  } else {
    console.warn(`Cannot show help image, not widget found with widget var ${HelpConfig.widgets.helpImage}`);
  }
}

/**
 * Registers the event listener to process messages from the help widget.
 */
function registerHelpWidgetMessageListener(): void {
  window.addEventListener("message", (event) => {
    const data = parseRecord(event.data);
    switch (data.action) {
      case "help-article-downvote":
        handleArticleDownvote();
        break;
      case "help-article-upvote":
        handleArticleUpvote();
        break;
      case "open-lightbox-image":
        handleOpenLightboxImage(data);
    }
  });
}

export function showHelpWidget(path: string = ""): void {
  // Check if we are in an iframe inside a backend page without a growl ourself
  // Send the message to the parent frame which can show the growl
  const framebusHandler = getFramebusInstance();
  if (framebusHandler instanceof FrameBusChildHandler) {
    framebusHandler.emitHelpDialog("show", path);
  } else {
    const widget = getWidgetByVar(HelpConfig.widgets.helpDialog, PrimeFaces.widget.Dialog);
    if (widget === undefined) {
      console.warn(`Cannot open help dialog, not widget found with widget var ${HelpConfig.widgets.helpDialog}`);
      return;
    }
    const iframe = widget.content.find("iframe");
    if (iframe.length > 0) {
      const origSrc = iframe.attr("data-original-src") ?? "";
      const url = new URL(origSrc);
      if (notEmpty(path)) {
        url.searchParams.set("path", path);
        iframe.attr("src", url.toString());
      } else {
        iframe.attr("src", origSrc);
      }
    }
    widget.show();
  }
}

/**
 * Attaches the requires JavaScript functionality to the help widget.
 */
export function initHelpWidget(config: IHelpWidgetConfig): void {
  HelpConfig = config;
  registerHelpWidgetMessageListener();
}
